import React, { useEffect, useMemo } from 'react';

import * as API from '../../../../../constants/api';
import client from '../../../../../services/library-api';
import assistantClient from '../../../../../services/assistant-api';

import { useFormState } from '../../../../../hooks/useFormState';
import { useFetchOptionsForPaginatedSelectWithSWR } from '../../../../../hooks/useFetchOptionsForPaginatedSelectWithSWR';
import { useSwrPageRequestKeysManagingWithContextSaving } from '../../../../../hooks/useSwrPageRequestKeysManagingWithContextSaving';

import { TAB_TYPE } from '../../../../../constants/playbooks';
import { emptyFieldErrorMessage } from '../../../../../constants/errorMessages';

import { capitalizeFirstLetter } from '../../../../../services/strings';
import { mergeNewDataIntoCurrent } from '../../../../../helpers/mergeNewDataIntoCurrent';
import { normalizeMarkdown, prepareMarkdownOnLoad } from '../../../../../helpers/normalizeMarkdown';

import Modal from '../../../../../design-system/Modal/Modal';
import ModalHeader from '../../../../../design-system/ModalHeader/ModalHeader';
import NewRichTextArea from '../../../../../design-system/NewRichTextArea/NewRichTextArea';
import PaginatedSelect from '../../../../../design-system/PaginatedSelect/PaginatedSelect';
import { Button, Input } from '../../../../../design-system';

const REQUIRED_FIELDS = {
    [TAB_TYPE.overview]: ['text'],
    [TAB_TYPE.form]: ['label', 'scenario'],
    [TAB_TYPE.prompt]: ['label', 'prompt'],
};

const TabEditorModal = ({ playbookId, mode, type, data, setTabs, setEditedTab, onClose }) => {
    const { formData, handleInputChange, fieldErrorMessages, setFieldErrorMessages } = useFormState(
        () => {
            if (mode === 'edit' && data) {
                const { label, detail } = data;
                const { text, scenario, chat_prompt } = detail || {};
                return { label, scenario, prompt: chat_prompt, text: prepareMarkdownOnLoad(text) };
            }

            return {
                text: '',
                scenario: null,
                prompt: null,
                label: type === TAB_TYPE.overview ? 'Overview' : '',
            };
        }
    );

    const {
        options: scenarioOptions,
        setOptions: setScenarioOptions,
        optionsLoading: scenarioOptionsLoading,
        totalOptions: totalScenarioOptions,
        setPage: setScenarioPage,
        canLoadMoreOptions: canLoadMoreScenarioOptions,
        isFirstRequestCompleted: isFirstScenarioRequestCompleted,
        key: scenariosKey,
    } = useFetchOptionsForPaginatedSelectWithSWR({
        client,
        route: API.ROUTES.library.scenario,
        searchParams: { playbook: playbookId },
        formatResponseToOptions: (results) => results?.map(({ id, name }) => ({ id, name })),
        autoFetchNextPage: false,
    });

    useSwrPageRequestKeysManagingWithContextSaving({
        pageAccessor: 'scenarios',
        key: scenariosKey,
    });

    const {
        options: promptOptions,
        setOptions: setPromptOptions,
        optionsLoading: promptOptionsLoading,
        totalOptions: totalPromptOptions,
        setPage: setPromptPage,
        canLoadMoreOptions: canLoadMorePromptOptions,
        isFirstRequestCompleted: isFirstPromptRequestCompleted,
        key: promptsKey,
    } = useFetchOptionsForPaginatedSelectWithSWR({
        client: assistantClient,
        route: API.ROUTES.assistant.chatPrompt,
        searchParams: { playbook: playbookId },
        formatResponseToOptions: (results) => results?.map(({ id, name }) => ({ id, name })),
        autoFetchNextPage: false,
    });

    useSwrPageRequestKeysManagingWithContextSaving({
        pageAccessor: 'chatPrompts',
        key: promptsKey,
    });

    useEffect(() => {
        if (mode === 'create') return;

        const scenarioId = formData.scenario;
        if (!scenarioId) return;

        const isScenarioInOptions = scenarioOptions.find(({ id }) => scenarioId === id);

        if (!isScenarioInOptions) {
            const fetchScenario = async () => {
                try {
                    const { data } = await client.get(
                        API.ROUTES.library.scenario + scenarioId + '/'
                    );
                    const { id, name } = data;
                    setScenarioOptions((options) =>
                        mergeNewDataIntoCurrent(options || [], [{ id, name }])
                    );
                } catch (e) {}
            };

            void fetchScenario();
        }
    }, []);

    useEffect(() => {
        if (mode === 'create') return;

        const chatPromptId = formData.prompt;
        if (!chatPromptId) return;

        const isChatPromptInOptions = promptOptions.find(({ id }) => chatPromptId === id);

        if (!isChatPromptInOptions) {
            const fetchChatPrompt = async () => {
                try {
                    const { data } = await assistantClient.get(
                        API.ROUTES.assistant.chatPrompt + chatPromptId + '/'
                    );
                    const { id, name } = data;
                    setPromptOptions((options) =>
                        mergeNewDataIntoCurrent(options || [], [{ id, name }])
                    );
                } catch (e) {}
            };

            void fetchChatPrompt();
        }
    }, []);

    const requiredFields = REQUIRED_FIELDS[type];

    const allRequiredFieldsFilled = useMemo(
        () => requiredFields.every((field) => !!formData[field]),
        [formData]
    );

    const submitButtonState = allRequiredFieldsFilled ? 'default' : 'disabled';

    const addNewTab = () => {
        const newTab = { type, is_live: true };

        if (type === TAB_TYPE.overview) {
            newTab.label = 'Overview';
            newTab.detail = { text: normalizeMarkdown(formData.text) };
        }

        if (type === TAB_TYPE.form) {
            newTab.label = formData.label;
            newTab.detail = { scenario: formData.scenario };
        }

        if (type === TAB_TYPE.prompt) {
            newTab.label = formData.label;
            newTab.detail = { chat_prompt: formData.prompt };
        }

        setTabs((tabs) => {
            const order = tabs.length + 1;
            return [...tabs, { ...newTab, order }];
        });
    };

    const editTab = () => {
        const editedPart = {};

        if (type === TAB_TYPE.overview) {
            editedPart.detail = { text: normalizeMarkdown(formData.text) };
        }

        if (type === TAB_TYPE.form) {
            editedPart.label = formData.label;
            editedPart.detail = { scenario: formData.scenario };
        }

        if (type === TAB_TYPE.prompt) {
            editedPart.label = formData.label;
            editedPart.detail = { chat_prompt: formData.prompt };
        }

        setEditedTab((tab) => ({ ...tab, ...editedPart }));
    };

    const handleSubmit = () => {
        try {
            if (!allRequiredFieldsFilled) {
                const errorMessages = requiredFields.reduce((acc, field) => {
                    if (field === 'scenario') {
                        return {
                            ...acc,
                            scenario: !formData[field] ? 'Please select a scenario' : null,
                        };
                    }
                    if (field === 'prompt') {
                        return {
                            ...acc,
                            prompt: !formData[field] ? 'Please select a prompt' : null,
                        };
                    }
                    return { ...acc, [field]: !formData[field] ? emptyFieldErrorMessage : null };
                }, {});
                setFieldErrorMessages(errorMessages);
                return;
            }

            const action = {
                create: addNewTab,
                edit: editTab,
            }[mode];

            action();
            onClose();
        } catch (e) {
            onClose();
        }
    };

    const title = {
        create: `Add ${capitalizeFirstLetter(type)} Tab`,
        edit: `Edit ${capitalizeFirstLetter(type)} Tab`,
    }[mode];

    const submitButtonText = {
        create: 'Add',
        edit: 'Save',
    }[mode];

    const optionsLoading = {
        [TAB_TYPE.form]: scenarioOptionsLoading,
        [TAB_TYPE.prompt]: promptOptionsLoading,
    }[type];

    const isFirstRequestCompleted = {
        [TAB_TYPE.form]: isFirstScenarioRequestCompleted,
        [TAB_TYPE.prompt]: isFirstPromptRequestCompleted,
    }[type];

    const totalOptions = {
        [TAB_TYPE.form]: totalScenarioOptions,
        [TAB_TYPE.prompt]: totalPromptOptions,
    }[type];

    const increaseComponentHeightBy =
        (optionsLoading && !isFirstRequestCompleted) || (totalOptions > 1 && totalOptions < 3)
            ? 60
            : totalOptions <= 1
            ? 30
            : 108;

    return (
        <Modal size="medium" onClose={onClose} resetPadding>
            <ModalHeader title={title} onClose={onClose} outlined />

            <div className="px-5 sm:px-6 md:px-8 py-4 md:py-5 flex flex-col gap-4">
                {type === TAB_TYPE.overview && (
                    <NewRichTextArea
                        value={formData.text}
                        label="Overview Text"
                        isRequired
                        setValue={(text) => handleInputChange('text', text)}
                        placeholder="Add your feedback or comments here!"
                        state={fieldErrorMessages.text ? 'error' : 'default'}
                        errorMessage={fieldErrorMessages.text}
                        minHeight={200}
                        darkBorderColor
                    />
                )}

                {(type === TAB_TYPE.form || type === TAB_TYPE.prompt) && (
                    <Input
                        size="sm"
                        value={formData.label}
                        name="tab_label"
                        label="Tab Label"
                        isRequired
                        onChange={(e) => handleInputChange('label', e.target.value)}
                        placeholder="Give your tab a unique name to help identify it"
                        state={fieldErrorMessages.label ? 'error' : 'default'}
                        errorMessage={fieldErrorMessages.label}
                    />
                )}

                {type === TAB_TYPE.form && (
                    <PaginatedSelect
                        size="sm"
                        name="scenario"
                        value={formData.scenario}
                        options={scenarioOptions}
                        onChange={(scenario) => handleInputChange('scenario', scenario)}
                        state={fieldErrorMessages.scenario ? 'error' : 'default'}
                        errorMessage={fieldErrorMessages.scenario}
                        label="Select Scenario"
                        isRequired
                        placeholder="Select a scenario for this form tab"
                        fetchOptions={() => setScenarioPage((page) => page + 1)}
                        canLoadMore={canLoadMoreScenarioOptions}
                        optionsLoading={scenarioOptionsLoading}
                        includeClientSideFiltering
                        totalOptionsCount={totalScenarioOptions}
                        dropdownHeight={180}
                        increaseComponentHeightBy={increaseComponentHeightBy}
                        isAdditionalLoaderDisplayed={scenarioOptionsLoading}
                    />
                )}

                {type === TAB_TYPE.prompt && (
                    <PaginatedSelect
                        size="sm"
                        name="prompt"
                        value={formData.prompt}
                        options={promptOptions}
                        onChange={(prompt) => handleInputChange('prompt', prompt)}
                        state={fieldErrorMessages.prompt ? 'error' : 'default'}
                        errorMessage={fieldErrorMessages.prompt}
                        label="Select Prompt"
                        isRequired
                        placeholder="Select a prompt for this form tab"
                        fetchOptions={() => setPromptPage((page) => page + 1)}
                        canLoadMore={canLoadMorePromptOptions}
                        optionsLoading={promptOptionsLoading}
                        includeClientSideFiltering
                        totalOptionsCount={totalPromptOptions}
                        dropdownHeight={180}
                        increaseComponentHeightBy={increaseComponentHeightBy}
                        isAdditionalLoaderDisplayed={promptOptionsLoading}
                    />
                )}

                <div className="flex items-center justify-between gap-3">
                    <Button size="md" type="neutral" text="Cancel" onClick={onClose} />
                    <Button
                        size="md"
                        type="secondary"
                        text={submitButtonText}
                        state={submitButtonState}
                        onClick={handleSubmit}
                    />
                </div>
            </div>
        </Modal>
    );
};

export default TabEditorModal;
